import { lazy } from "react";

/*
 * lazyWithRetry — drop-in de React.lazy resiliente a fallos de carga de chunk.
 *
 * Problema que resuelve: con `lazy(() => import(...))` a secas, si el import()
 * del chunk falla UNA vez (timeout por red lenta, o el index.html quedó viejo
 * apuntando a un chunk que un deploy nuevo ya borró), la promesa se rechaza y
 * el <Suspense> se queda mostrando el spinner PARA SIEMPRE, sin reintentar ni
 * avisar. Ese es el "queda en loading" al tocar "Editar menú".
 *
 * Estrategia (de menos a más agresiva):
 *   1) Reintenta el import() una vez con una pausa corta (cubre hipos de red
 *      transitorios que fallan rápido — la causa más común).
 *   2) Si igual falla y parece un ChunkLoadError, recarga la página UNA sola vez
 *      por sesión: casi siempre el HTML quedó viejo y recargar trae el index
 *      nuevo con los nombres de chunk correctos. OJO: solo recargamos si podemos
 *      PERSISTIR el guard en sessionStorage; si el storage está bloqueado no
 *      podríamos frenar el loop, así que en ese caso NO recargamos y vamos
 *      directo al mensaje de error.
 *   3) Si ya se recargó (o no es un fallo de chunk, o no hay storage), rechaza la
 *      promesa para que el ChunkErrorBoundary muestre un mensaje claro en vez del
 *      spinner eterno.
 *
 * En el camino feliz (import OK al primer intento) el comportamiento es idéntico
 * a React.lazy: mismo módulo, misma promesa resuelta, sin demoras.
 *
 * Nota sobre el "hang" de red: si la petición del chunk queda colgada sin error,
 * webpack la rechaza recién a los ~120s (su chunkLoadTimeout). No le imponemos un
 * timeout propio más corto a propósito: cortar antes podría abortar una carga
 * legítimamente lenta (conexión mala pero funcional) y romperle la pantalla a un
 * cliente que hoy anda. Preferimos ser pacientes; el peor caso sigue terminando
 * en el mensaje de error en vez del spinner infinito de antes.
 */

// Detecta el error típico de "no pude bajar el chunk" del import() dinámico.
// Cubre webpack (CRA), Vite y errores de red de la carga del <script>.
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || "");
  const message = String(error.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading( CSS)? chunk [\w-]+ failed/i.test(message) ||
    /failed to (fetch|import) dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

// Guard de sesión para el reload de rescate. Clave PROPIA, distinta de la del
// auto-update de index.html ("ra_reloaded_ver"), para no interferir entre sí.
export const RESCUE_KEY = "ra_chunk_rescue_reloaded";

function sessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function sessionSet(key, val) {
  try {
    window.sessionStorage.setItem(key, val);
  } catch (_) {
    /* storage bloqueado: el set no persiste; lo detectamos con el read-back */
  }
}

// Marca el guard y verifica que REALMENTE quedó persistido. Devuelve true solo
// si podemos confiar en que, tras el reload, vamos a poder leerlo de vuelta y
// NO recargar de nuevo. Si el storage está bloqueado, devuelve false → no hay
// forma segura de frenar un loop, así que el llamador NO debe recargar.
function markRescuePersisted() {
  sessionSet(RESCUE_KEY, "true");
  return sessionGet(RESCUE_KEY) === "true";
}

export default function lazyWithRetry(importFn, { retries = 1, interval = 500 } = {}) {
  return lazy(
    () =>
      new Promise((resolve, reject) => {
        const attempt = (remaining) => {
          // Promise.resolve().then(importFn) captura también un throw sincrónico
          // de importFn, no solo un rechazo del import().
          Promise.resolve()
            .then(importFn)
            .then(resolve)
            .catch((error) => {
              if (remaining > 0) {
                setTimeout(() => attempt(remaining - 1), interval);
                return;
              }
              // Sin más reintentos. Solo intentamos el reload de rescate si:
              //  - parece un fallo de chunk,
              //  - podemos recargar,
              //  - todavía no recargamos en esta sesión, y
              //  - podemos PERSISTIR el guard (si no, recargar sería un loop).
              const canReload =
                typeof window !== "undefined" &&
                window.location &&
                typeof window.location.reload === "function";
              if (
                isChunkLoadError(error) &&
                canReload &&
                sessionGet(RESCUE_KEY) !== "true" &&
                markRescuePersisted()
              ) {
                window.location.reload();
                return; // promesa pendiente a propósito: la página se recarga
              }
              // Ya recargamos, no es un fallo de chunk, o no hay storage donde
              // frenar el loop: que lo tome el ChunkErrorBoundary y muestre un
              // mensaje claro en vez de recargar para siempre.
              reject(error);
            });
        };
        attempt(retries);
      })
  );
}
